import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS, INLINES, Document } from "@contentful/rich-text-types";
import { localizeUrl } from "@/lib/url/localizeUrl";
import { DEFAULT_PUBLIC_LANG } from "@/lib/locale/toContentfulLocale";
interface ButtonData {
  textLabel?: string;
  url?: string;
  ariaLabel?: string;
}
const secondaryButtonOnBlueClasses = "t2-button t2-button--medium t2-button--secondary border-2 !border-white bg-transparent !text-white shadow-none hover:border-white hover:text-white";
function createCtaDescriptionOptions(lang: string) {
  return {
    renderMark: {
      [MARKS.BOLD]: (text: React.ReactNode) => <strong>{text}</strong>,
      [MARKS.ITALIC]: (text: React.ReactNode) => <em>{text}</em>,
      [MARKS.UNDERLINE]: (text: React.ReactNode) => <u>{text}</u>
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node: unknown, children: React.ReactNode) => <p className="mb-4 text-center text-base font-normal leading-7 text-white last:mb-0">
          {children}
        </p>,
      [INLINES.HYPERLINK]: (node: {
        data?: {
          uri?: string;
        };
      }, children: React.ReactNode) => {
        const uri = node?.data?.uri;
        if (!uri) return <span>{children}</span>;
        const localizedUri = localizeUrl(uri, lang);
        const isExternal = uri.startsWith("http");
        return <Link href={localizedUri} className="text-white underline" target={isExternal ? "_blank" : "_self"} rel={isExternal ? "noopener noreferrer" : undefined}>
            {children}
          </Link>;
      }
    }
  };
}
function CtaButton(t0) {
  const $ = _c(10);
  const {
    button,
    lang,
    variant
  } = t0;
  if (!button?.textLabel || !button?.url) {
    return null;
  }
  let t1;
  if ($[0] !== button.url || $[1] !== lang) {
    t1 = localizeUrl(button.url, lang);
    $[0] = button.url;
    $[1] = lang;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const href = t1;
  const isExternal = button.url.startsWith("http");
  const className = variant === "primary" ? "t2-button t2-button--medium t2-button--primary" : secondaryButtonOnBlueClasses;
  const t2 = button.ariaLabel ?? undefined;
  const t3 = isExternal ? "_blank" : undefined;
  const t4 = isExternal ? "noopener noreferrer" : undefined;
  let t5;
  if ($[3] !== button.textLabel || $[4] !== className || $[5] !== href || $[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <Link href={href} className={className} aria-label={t2} target={t3} rel={t4}>{button.textLabel}</Link>;
    $[3] = button.textLabel;
    $[4] = className;
    $[5] = href;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
export default function ArticleCta(props) {
  const $ = _c(12);
  const {
    data,
    lang: t0
  } = props;
  const lang = t0 === undefined ? DEFAULT_PUBLIC_LANG : t0;
  const primary = data?.primaryButton;
  const secondary = data?.secondaryButton;
  const descriptionJson = data?.description?.json;
  const hasDescription = !!descriptionJson;
  const hasButtons = primary?.textLabel && primary?.url || secondary?.textLabel && secondary?.url;
  if (!hasDescription && !hasButtons) {
    return null;
  }
  let t1;
  if ($[0] !== descriptionJson || $[1] !== hasDescription || $[2] !== lang) {
    t1 = hasDescription && <div className="article-cta__description mb-6">{documentToReactComponents(descriptionJson, createCtaDescriptionOptions(lang))}</div>;
    $[0] = descriptionJson;
    $[1] = hasDescription;
    $[2] = lang;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== hasButtons || $[5] !== lang || $[6] !== primary || $[7] !== secondary) {
    t2 = hasButtons && <div className="article-cta__buttons flex flex-wrap items-center justify-center gap-4"><CtaButton button={primary} lang={lang} variant="primary" /><CtaButton button={secondary} lang={lang} variant="secondary" /></div>;
    $[4] = hasButtons;
    $[5] = lang;
    $[6] = primary;
    $[7] = secondary;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <div className="article-cta rounded-lg bg-blue-trmb px-6 py-8 text-white">{t1}{t2}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}